// 🔧 public_route.js
// ✅ Purpose: Allow only unauthenticated users to access public auth pages
// ✅ Features:
// - Shows children if user is not logged in
// - Keeps unverified users logged in and sends them to Dashboard
// - Preserves pending vote flow after verification
// - Prevents logged-in verified users from returning to login/register
// - Supports new workflow: Register → Dashboard → Verify Email → Vote

import React from "react";
import { Navigate } from "react-router";
import { useAuth } from "./auth_context";
import FullPageSpinner from "./components/FullPageSpinner";
import {
  SESSION_KEYS,
  getSessionValue,
} from "./utils/browser_storage_helpers";

const PublicRoute = ({ children, allowGuests = false }) => {
  const { user, authLoading } = useAuth();

  if (authLoading) {
    return <FullPageSpinner message="Checking login status..." />;
  }

  // ✅ If no user is logged in, allow login/register pages to show.
  if (!user) {
    return <>{children}</>;
  }

  // ✅ If user is logged in but NOT verified, keep them logged in
  // and send them to Dashboard where the verification panel lives.
  if (user && !user.emailVerified) {
    return <Navigate to="/dashboard?verifyRequired=1" replace />;
  }

  // ✅ If user is verified and has a pending vote, return them to that vote.
  const pendingCid = getSessionValue(
    SESSION_KEYS.PENDING_VOTE_CID
  );

  const eventSlug = getSessionValue(
    SESSION_KEYS.LAST_EVENT_SLUG
  );

  if (pendingCid && eventSlug) {
    return <Navigate to={`/${eventSlug}/vote/${pendingCid}`} replace />;
  }

  // ✅ Otherwise, logged-in users should land on Dashboard.
  return <Navigate to="/dashboard?pr" replace />;
};

export default PublicRoute;